import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Plus, Sparkles, Star, X } from "lucide-react";

import type { CrmContact, CrmEmailDraft, CrmLead, OutboundEmail } from "@/types/crm";
import type { EmailLog, EmailTemplateOption, SendEmailPayload } from "@/types/email";
import { TemplatePicker } from "@/components/email/TemplatePicker";
import { EmailLogList } from "@/components/email/EmailLogList";
import { useEmailClient } from "@/hooks/useEmailClient";
import { useHitlActions } from "@/hooks/useHitlActions";
import {
  applyLeadOutreachTemplate,
  fetchLeadOutboundEmails,
  fetchLeadOutreach,
  fetchTemplateVariables,
  saveLeadOutreachDraft,
  sendLeadOutreach,
} from "@/api/crm";
import { fetchEffectiveEmailSender, type EmailSenderAccount } from "@/api/emailSettings";
import { TemplateManagerDrawer } from "@/components/email/TemplateManagerDrawer";
import { fetchMyArtist } from "@/api/artistSettings";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useToast } from "@/components/ToastProvider";
import CapabilityGate from "@/components/CapabilityGate";
import { useCapabilities } from "@/hooks/useCapabilities";
import {
  TemplateWarnings,
  extractUnknownPlaceholders,
  highlightUnknownPlaceholders,
} from "@/components/templates/TemplateWarnings";
import { getEventWindowFromMetadata } from "@/lib/booking";
import { sanitizeTemplateHtml } from "@/lib/safeHtml";
import type { ArtistSettings } from "@/types/artistSettings";

type EmailDrawerProps = {
  open: boolean;
  onClose: () => void;
  contact?: CrmContact | null;
  lead?: CrmLead | null;
  initialSubject?: string | null;
  initialBody?: string | null;
  draftEmailId?: number | null;
  draft?: CrmEmailDraft | null;
  enableAIFeatures?: boolean;
  contextOverrides?: Record<string, string>;
  onUpgradeRequest?: () => void;
  sequenceControls?: {
    index: number;
    total: number;
    onPrev?: () => void;
    onNext?: () => void;
  };
  /**
   * Musician outreach mode (Booking). Templates are filled server-side by the CRM variable
   * registry, drafts are the lead's outbound_emails record (shared with Discover Pitch), and
   * the footer offers Save draft / Copy / Send / Mark contacted. No AI or email_logs.
   */
  outreach?: {
    leadId: number;
    contacted?: boolean;
    onMarkContacted?: () => Promise<void> | void;
  };
};

type ContextRow = { key: string; value: string };

type CapabilityBlockedError = {
  code?: string;
  status?: number;
  capability?: string;
  payload?: { capability?: string; error?: string } | null;
};

function getPrimarySourceUrl(contact?: { source_url?: string | null; source_urls?: string[] | null } | null): string | null {
  const direct = contact?.source_url?.trim();
  if (direct) return direct;
  const list = contact?.source_urls;
  if (!Array.isArray(list)) return null;
  const first = list.find((url) => typeof url === "string" && url.trim().length > 0);
  return first ? first.trim() : null;
}

function normalizeExternalUrl(url: string | null): string | null {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function isCapabilityBlockedError(error: unknown): error is CapabilityBlockedError {
  if (!error || typeof error !== "object") return false;
  if ((error as CapabilityBlockedError).code === "capability_required") return true;
  if ((error as CapabilityBlockedError).status === 403) {
    const payload = (error as CapabilityBlockedError).payload;
    if (payload && payload.error === "requires_pro") return true;
  }
  return false;
}

function capabilityFromError(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const direct = (error as CapabilityBlockedError).capability;
  if (typeof direct === "string" && direct) return direct;
  const payload = (error as CapabilityBlockedError).payload;
  if (payload && typeof payload.capability === "string" && payload.capability) {
    return payload.capability;
  }
  return null;
}

export function EmailDrawer({
  open,
  onClose,
  contact,
  lead,
  initialSubject,
  initialBody,
  draftEmailId: initialDraftId,
  draft = null,
  enableAIFeatures = true,
  contextOverrides,
  onUpgradeRequest,
  sequenceControls,
  outreach,
}: EmailDrawerProps) {
  const { push } = useToast();
  const isOutreach = Boolean(outreach);
  const aiEnabled = enableAIFeatures !== false && !isOutreach;
  const {
    templates,
    templatesLoading,
    logs,
    logsLoading,
    sending,
    sendEmail,
    sendTest,
    generateDraft,
    favoriteDraft,
    refreshTemplates,
  } = useEmailClient({
    contactId: contact?.id,
    leadId: lead?.id,
    includeLogs: !isOutreach,
  });
  const [fill, setFill] = useState<{ missing: string[]; unknown: string[] } | null>(null);
  const [templateVersionId, setTemplateVersionId] = useState<number | null>(null);
  const [outreachHistory, setOutreachHistory] = useState<OutboundEmail[]>([]);
  const [outreachBusy, setOutreachBusy] = useState<"loading" | "applying" | "saving" | "sending" | "marking" | null>(null);
  const [outreachMessage, setOutreachMessage] = useState<string | null>(null);
  const [outreachDirty, setOutreachDirty] = useState(false);
  const [copied, setCopied] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [effectiveSender, setEffectiveSender] = useState<EmailSenderAccount | null>(null);
  const [outreachSent, setOutreachSent] = useState(false);
  const [outreachIdempotencyKey, setOutreachIdempotencyKey] = useState<string | null>(null);
  const [recommendedTemplateKind, setRecommendedTemplateKind] = useState<string | null>(null);

  const [artistProfile, setArtistProfile] = useState<ArtistSettings | null>(null);
  const defaultContext = useMemo(
    () => mergeContext(deriveDefaultContext(contact, lead), contextOverrides),
    [contact, lead, contextOverrides],
  );
  const artistContext = useMemo(() => buildArtistContext(artistProfile), [artistProfile]);
  const [contextRows, setContextRows] = useState<ContextRow[]>(() => entriesFromContext(defaultContext));
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [subjectInput, setSubjectInput] = useState("");
  const [bodyInput, setBodyInput] = useState("");
  const [toInput, setToInput] = useState(contact?.email ?? "");
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [bodyTouched, setBodyTouched] = useState(false);
  const [draftEmailId, setDraftEmailId] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [previewLog, setPreviewLog] = useState<EmailLog | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { capabilities, openUpgradeModal } = useCapabilities();
  const crmEmailAIEnabled = Boolean(capabilities.canUseCrmEmailAI ?? capabilities.canUseEmailAI);
  const crmTemplateAutomationEnabled = Boolean(
    capabilities.canUseCrmTemplateAutomation ?? capabilities.canUseTemplateAutomation,
  );
  const allowEmailAI = aiEnabled && crmEmailAIEnabled;
  const allowTemplateAutomation = aiEnabled && crmTemplateAutomationEnabled;
  const allowEmailRevisionTools =
    aiEnabled &&
    Boolean(capabilities.canUseEmailRevisionTools ?? capabilities.canUseCrmEmailAI ?? capabilities.canUseEmailAI);
  const [variableRegistry, setVariableRegistry] = useState<Record<string, string>>({});
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registryError, setRegistryError] = useState<string | null>(null);
  const logFilterAddress = useMemo(
    () => contact?.email?.trim() || toInput.trim(),
    [contact?.email, toInput],
  );
  const contactSourceUrl = useMemo(() => getPrimarySourceUrl(contact), [contact]);
  const contactSourceHref = useMemo(() => normalizeExternalUrl(contactSourceUrl), [contactSourceUrl]);
  const scopedLogs = useMemo(() => filterLogsByAddress(logs, logFilterAddress), [logs, logFilterAddress]);
  const requestUpgrade = useCallback(
    (feature: string) => {
      onUpgradeRequest?.();
      openUpgradeModal(feature);
    },
    [onUpgradeRequest, openUpgradeModal],
  );
  const handleCapabilityBlock = useCallback(
    (error: unknown, fallback?: string) => {
      if (!isCapabilityBlockedError(error)) return;
      const capability = capabilityFromError(error);
      openUpgradeModal(capability ?? fallback ?? "CRM email AI");
    },
    [openUpgradeModal],
  );

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  );
  const handleInsertToken = useCallback(
    (token: string) => {
      if (selectedTemplate) {
        push({
          title: "Template locked",
          message: "Detach from the template to edit the body and insert tokens.",
        });
        return;
      }
      setBodyTouched(true);
      setBodyInput((prev) => `${prev || ""} ${token}`.trim());
    },
    [push, selectedTemplate],
  );

  const artistAssetTokens = [
    "[artist_logo_url]",
    "[artist_band_photo_url]",
    "[artist_video_link_1]",
    "[artist_video_link_2]",
    "[artist_video_link_3]",
    "[artist_audio_link_1]",
    "[artist_audio_link_2]",
    "[artist_audio_link_3]",
  ];
  const artistSocialTokens = [
    "[artist_website]",
    "[artist_instagram]",
    "[artist_facebook]",
    "[artist_x_url]",
    "[artist_youtube_url]",
    "[artist_tiktok]",
    "[artist_spotify_url]",
    "[artist_apple_music_url]",
    "[artist_bandcamp_url]",
    "[artist_bandsintown_url]",
  ];

  const contextMap = useMemo(() => {
    const entries: [string, string][] = [];
    contextRows.forEach((row) => {
      if (!row.key.trim()) return;
      entries.push([row.key.trim(), row.value]);
    });
    return mergeContext(Object.fromEntries(entries), artistContext);
  }, [contextRows, artistContext]);

  const selectedDraftTemplate = useMemo(() => {
    if (!draft?.template_version_id) return null;
    return (
      templates.find((template) => Number(template.version_id) === Number(draft.template_version_id)) ?? null
    );
  }, [templates, draft?.template_version_id]);
  const diagnosticsTemplate = selectedTemplate ?? selectedDraftTemplate;

  useEffect(() => {
    if (!open || !outreach) return;
    let cancelled = false;
    setOutreachBusy("loading");
    setFill(null);
    setOutreachMessage(null);
    setOutreachDirty(false);
    setSelectedTemplateId(null);
    setOutreachSent(false);
    setOutreachIdempotencyKey(null);
    Promise.allSettled([
      fetchLeadOutreach(outreach.leadId),
      fetchLeadOutboundEmails(outreach.leadId),
      fetchEffectiveEmailSender(),
    ])
      .then(([state, history, sender]) => {
        if (cancelled) return;
        if (state.status === "fulfilled") {
          setSubjectInput(state.value.draft?.subject ?? "");
          setBodyInput(state.value.draft?.body ?? "");
          setToInput(state.value.draft?.recipient ?? state.value.contact.email ?? contact?.email ?? "");
          setRecommendedTemplateKind(state.value.recommended_template_kind ?? null);
        } else {
          setOutreachMessage("We couldn't load this draft. Please try again.");
        }
        setOutreachHistory(history.status === "fulfilled" ? history.value ?? [] : []);
        setEffectiveSender(sender.status === "fulfilled" ? sender.value : null);
      })
      .finally(() => {
        if (!cancelled) setOutreachBusy(null);
      });
    return () => {
      cancelled = true;
    };
    // Load once per open/lead; contact arrives separately and must not reset edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, outreach?.leadId]);

  useEffect(() => {
    if (!open || outreach) return;
    setContextRows(entriesFromContext(defaultContext));
    setSelectedTemplateId(null);
    setSubjectInput(initialSubject ?? "");
    setBodyInput(initialBody ?? "");
    setToInput(contact?.email ?? "");
    setSubjectTouched(initialSubject != null);
    setBodyTouched(initialBody != null);
    setDraftEmailId(initialDraftId ?? null);
    setPreviewLog(null);
    setMobileSidebarOpen(false);
  }, [open, defaultContext, contact?.email, initialSubject, initialBody, initialDraftId]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setRegistryLoading(true);
    setRegistryError(null);
    fetchTemplateVariables("crm")
      .then((payload) => {
        setVariableRegistry(payload?.variables ?? {});
      })
      .catch((error) => {
        setRegistryError(error instanceof Error ? error.message : "Unable to load variable registry.");
      })
      .finally(() => {
        setRegistryLoading(false);
      });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchMyArtist()
      .then((payload) => {
        if (!cancelled) {
          setArtistProfile(payload ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setArtistProfile(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!selectedTemplate || isOutreach) return;
    setDraftEmailId(null);
    if (!subjectTouched) {
      setSubjectInput(renderTemplate(selectedTemplate.subject, contextMap));
    }
    if (!bodyTouched) {
      setBodyInput(renderTemplate(selectedTemplate.body_html, contextMap));
    }
  }, [selectedTemplate, contextMap, subjectTouched, bodyTouched, isOutreach]);

  const templateLocked = Boolean(selectedTemplate) && !isOutreach;
  // Outreach fills from each template's newest active version server-side: list one entry per template.
  const pickerTemplates = useMemo(() => {
    if (!isOutreach) return templates;
    const newest = new Map<number, EmailTemplateOption>();
    templates
      .filter((option) => option.active !== false)
      .forEach((option) => {
        const key = Number(option.template_id);
        const current = newest.get(key);
        if (!current || Number(option.version_id) > Number(current.version_id)) newest.set(key, option);
      });
    return Array.from(newest.values()).map((option) => ({
      ...option,
      recommended: option.metadata?.booking_template_kind === recommendedTemplateKind,
    }));
  }, [isOutreach, recommendedTemplateKind, templates]);
  const hasOutreachDraft = Boolean(subjectInput.trim() || bodyInput.trim());

  const handleApplyOutreachTemplate = async (template: EmailTemplateOption) => {
    if (!outreach) return;
    if (hasOutreachDraft && !window.confirm("Replace the current subject and message with this template?")) return;
    setOutreachBusy("applying");
    setOutreachMessage(null);
    try {
      const result = await applyLeadOutreachTemplate(outreach.leadId, Number(template.template_id));
      setSubjectInput(result.subject);
      setBodyInput(result.body);
      setTemplateVersionId(result.template_version_id);
      setFill({ missing: result.missing, unknown: result.unknown });
      setOutreachDirty(true);
    } catch {
      setOutreachMessage("We couldn't use that template. Please try again.");
    } finally {
      setOutreachBusy(null);
    }
  };

  const saveOutreachDraft = async () => {
    if (!outreach) return;
    await saveLeadOutreachDraft(outreach.leadId, {
      subject: subjectInput,
      body: bodyInput,
      recipient: toInput.trim() || null,
      template_version_id: templateVersionId,
    });
    setOutreachDirty(false);
  };

  const handleSaveOutreachDraft = async () => {
    setOutreachBusy("saving");
    setOutreachMessage(null);
    try {
      await saveOutreachDraft();
      setOutreachMessage("Draft saved");
      if (outreach) setOutreachHistory((await fetchLeadOutboundEmails(outreach.leadId)) ?? []);
    } catch {
      setOutreachMessage("We couldn't save your draft. Please try again.");
    } finally {
      setOutreachBusy(null);
    }
  };

  const handleCopyOutreach = async () => {
    const subject = subjectInput.trim();
    const body = bodyInput.trim();
    try {
      await navigator.clipboard.writeText(subject ? `Subject: ${subject}\n\n${body}` : body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setOutreachMessage("Copy didn't work in this browser. Select the text and copy it manually.");
    }
  };

  const handleMarkContacted = async () => {
    if (!outreach?.onMarkContacted) return;
    setOutreachBusy("marking");
    setOutreachMessage(null);
    try {
      if (outreachDirty && hasOutreachDraft) await saveOutreachDraft();
      await outreach.onMarkContacted();
      setOutreachMessage("Marked contacted. This booking is now in Outreach.");
    } catch {
      setOutreachMessage("We couldn't update this booking. Please try again.");
    } finally {
      setOutreachBusy(null);
    }
  };

  const handleSendOutreach = async () => {
    if (!outreach || !readyToSend || effectiveSender?.verification_status !== "connected") return;
    const from = effectiveSender.from_name
      ? `${effectiveSender.from_name} <${effectiveSender.email_address}>`
      : effectiveSender.email_address;
    if (!window.confirm(`Send this email from ${from} to ${toInput.trim()}?`)) return;
    setOutreachBusy("sending");
    setOutreachMessage(null);
    try {
      const idempotencyKey = outreachIdempotencyKey
        ?? (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${outreach.leadId}`);
      setOutreachIdempotencyKey(idempotencyKey);
      const result = await sendLeadOutreach(outreach.leadId, {
        subject: subjectInput.trim(),
        body: bodyInput,
        recipient: toInput.trim(),
        template_version_id: templateVersionId,
        idempotency_key: idempotencyKey,
      });
      setOutreachDirty(false);
      setOutreachSent(result.crm_sync?.status !== "pending");
      setOutreachHistory((await fetchLeadOutboundEmails(outreach.leadId)) ?? []);
      setOutreachMessage(
        result.crm_sync?.status === "pending"
          ? "Email accepted, but Booking could not update yet. Do not resend; retry this action to repair the Booking stage."
          : "Email sent. This booking is now in Outreach.",
      );
    } catch (err) {
      setOutreachIdempotencyKey(null);
      setOutreachMessage(err instanceof Error ? err.message : "We couldn't send this email. Please try again.");
    } finally {
      setOutreachBusy(null);
    }
  };

  const unresolvedPlaceholders = Array.from(
    `${subjectInput}\n${bodyInput}`.matchAll(/\{\{\s*(?:UNKNOWN:)?([^{}]+?)\s*\}\}|\[([a-zA-Z][a-zA-Z0-9_.-]*)\]/g),
  );
  const readyToSend = toInput.trim().length > 3 && Boolean(subjectInput.trim() && bodyInput.trim()) && unresolvedPlaceholders.length === 0;
  const canGenerateDraft = Boolean(lead?.id);
  const showFavoriteAction = Boolean(draftEmailId);

  const buildPayload = useCallback((): SendEmailPayload => {
    const contextPayload = Object.keys(contextMap).length ? contextMap : undefined;
    return {
      to: toInput.trim(),
      context: contextPayload,
      subject: subjectInput.trim(),
      body_html: bodyInput,
      contact_id: contact?.id,
      lead_id: lead?.id,
    };
  }, [contextMap, toInput, subjectInput, bodyInput, contact?.id, lead?.id]);

  const unknownPlaceholderTokens = useMemo(() => {
    const matches = new Set<string>();
    [...extractUnknownPlaceholders(subjectInput), ...extractUnknownPlaceholders(bodyInput)].forEach((token) =>
      matches.add(token),
    );
    detectBracketTokens(subjectInput).forEach((token) => matches.add(token));
    detectBracketTokens(bodyInput).forEach((token) => matches.add(token));
    return Array.from(matches);
  }, [subjectInput, bodyInput]);

  const templateVariables = useMemo(() => {
    const metadata = (diagnosticsTemplate?.metadata?.template_variables ?? null) as
      | { all?: string[]; unknown?: string[] }
      | null;
    const detected = new Set<string>(Array.isArray(metadata?.all) ? metadata?.all : []);
    const unknown = new Set<string>(Array.isArray(metadata?.unknown) ? metadata?.unknown : []);
    const known = new Set<string>();
    detected.forEach((token) => {
      if (!unknown.has(token)) {
        known.add(token);
      }
    });
    unknownPlaceholderTokens.forEach((token) => {
      detected.add(token);
      unknown.add(token);
      known.delete(token);
    });
    if (!known.size && detected.size) {
      detected.forEach((token) => {
        if (!unknown.has(token)) {
          known.add(token);
        }
      });
    }
    if (!detected.size && !unknown.size && !unknownPlaceholderTokens.length) {
      return null;
    }
    return {
      detected: Array.from(detected.size ? detected : new Set<string>(unknownPlaceholderTokens)),
      known: Array.from(known),
      unknown: Array.from(unknown.size ? unknown : new Set<string>(unknownPlaceholderTokens)),
    };
  }, [diagnosticsTemplate?.metadata, unknownPlaceholderTokens]);

  const templateWarningEntries = useMemo(
    () =>
      unknownPlaceholderTokens.map((token) => ({
        type: "unknown_variable",
        variable: token,
        message: undefined,
      })),
    [unknownPlaceholderTokens],
  );
  const hitlActions = useHitlActions(lead?.hitl_id ?? undefined);
  const revisionDiff = useMemo(() => {
    if (!allowEmailRevisionTools) return [];
    const previous = draft?.rendered_body ?? previewLog?.body_html ?? null;
    if (!previous) return [];
    return buildLineDiff(previous, bodyInput);
  }, [allowEmailRevisionTools, bodyInput, draft?.rendered_body, previewLog?.body_html]);
  const emailAnalytics = useMemo(() => {
    if (!allowEmailRevisionTools || !scopedLogs.length) return null;
    const opens = scopedLogs.filter((log) => Boolean(log.opened_at)).length;
    const total = scopedLogs.length;
    return {
      total,
      opens,
      openRate: total ? opens / total : 0,
    };
  }, [allowEmailRevisionTools, scopedLogs]);

  const handleSend = async () => {
    if (!readyToSend) {
      push({ title: "Missing fields", message: "Add a recipient and content before sending." });
      return;
    }
    try {
      await sendEmail(buildPayload());
    } catch (error) {
      handleCapabilityBlock(error, "CRM email AI");
    }
  };

  const handleGenerateDraft = async () => {
    if (!allowEmailAI) {
      requestUpgrade("Email AI");
      return;
    }
    if (!lead?.id) {
      push({ title: "Lead required", message: "Link this contact to a lead before generating a draft." });
      return;
    }
    setGenerating(true);
    try {
      const draft = await generateDraft({
        lead_id: lead.id,
        template_id: selectedTemplate?.template_id,
      });
      setSubjectInput(draft.rendered_subject ?? "");
      setBodyInput(draft.rendered_body ?? "");
      setSubjectTouched(true);
      setBodyTouched(true);
      setSelectedTemplateId(null);
      setDraftEmailId(draft.id);
    } catch (error) {
      handleCapabilityBlock(error, "CRM email AI");
    } finally {
      setGenerating(false);
    }
  };

  const handleFavoriteDraft = async () => {
    if (!allowTemplateAutomation) {
      requestUpgrade("Template automation");
      return;
    }
    if (!draftEmailId) {
      push({ title: "No draft", message: "Generate or open an AI draft before favoriting." });
      return;
    }
    const templateName = window.prompt("Template name", subjectInput || contact?.name || "New template");
    if (!templateName) return;
    const notes = window.prompt("Why is this draft useful for future automations?", "") ?? undefined;
    setFavoriteBusy(true);
    try {
      await favoriteDraft(draftEmailId, {
        name: templateName,
        persona: lead?.agent_owner ?? undefined,
        notes,
      });
    } catch (error) {
      handleCapabilityBlock(error, "CRM template automation");
    } finally {
      setFavoriteBusy(false);
    }
  };

  const handleRequestReview = useCallback(async () => {
    if (!allowEmailAI) {
      requestUpgrade("Email AI");
      return;
    }
    if (!lead?.hitl_id) {
      push({ title: "HITL unavailable", message: "Lead is not linked to the approvals queue." });
      return;
    }
    const summary = `Email draft ready for review.\nSubject: ${subjectInput || "(no subject)"}\n\n${stripHtml(
      bodyInput,
    ).slice(0, 800)}`;
    try {
      await hitlActions.requestChanges(summary);
      push({ title: "Review requested", message: "Draft sent to the HITL inbox." });
    } catch (error) {
      handleCapabilityBlock(error, "CRM email AI");
    }
  }, [allowEmailAI, bodyInput, hitlActions, lead?.hitl_id, push, requestUpgrade, subjectInput]);

  const handleAutoFillVariables = useCallback(() => {
    const targets = unknownPlaceholderTokens.length ? unknownPlaceholderTokens : Object.keys(variableRegistry);
    if (!targets.length) return;
    setContextRows((rows) => {
      const next = [...rows];
      targets.forEach((token) => {
        if (!token || next.some((row) => row.key.trim() === token)) {
          return;
        }
        const fallback = contextMap[token] ?? defaultContext[token] ?? variableRegistry[token] ?? "";
        next.push({ key: token, value: String(fallback ?? "") });
      });
      return next;
    });
  }, [contextMap, defaultContext, unknownPlaceholderTokens, variableRegistry]);

  if (!open) {
    return null;
  }

  const sidebarInsights = isOutreach ? (
    <div className="space-y-3" data-testid="outreach-history">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">Outreach for this booking</p>
      {outreachHistory.length === 0 ? (
        <p className="text-xs text-muted-foreground">No saved drafts yet.</p>
      ) : (
        <ul className="space-y-2 text-xs">
          {outreachHistory.map((item) => (
            <li key={item.id} className="rounded-lg border border-border bg-background/40 p-2">
              <p className="font-medium text-foreground">{item.rendered_subject || "(no subject)"}</p>
              <p className="text-muted-foreground">
                {item.status === "draft" ? "Draft" : item.status}
                {item.updated_at ? ` · ${new Date(item.updated_at).toLocaleDateString()}` : ""}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : (
    <div className="space-y-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">Recent emails</p>
      {aiEnabled && (
        <CapabilityGate flag="canUseCrmEmailAI">
          <CapabilityGate flag="canUseEmailRevisionTools" variant="hide">
            {emailAnalytics && (
              <div className="aa-glass-card p-4 text-xs text-foreground/70">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Delivery analytics</p>
                <p className="mt-1 text-foreground">
                  {emailAnalytics.opens} of {emailAnalytics.total} opened ({Math.round(emailAnalytics.openRate * 100)}%)
                </p>
              </div>
            )}
          </CapabilityGate>
        </CapabilityGate>
      )}
      <EmailLogList logs={scopedLogs} loading={logsLoading} onSelect={(log) => setPreviewLog(log)} />
      {previewLog && (
        <section className="aa-glass-card space-y-3 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Viewing email</p>
              <p className="text-sm font-semibold text-foreground">{previewLog.subject}</p>
              <p className="text-xs text-muted-foreground">
                {previewLog.direction === "inbound"
                  ? `From ${previewLog.from_address ?? "unknown sender"}`
                  : `To ${previewLog.to_address ?? "unknown recipient"}`}
              </p>
            </div>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setPreviewLog(null)}
            >
              Close
            </button>
          </div>
          <div className="rounded-xl border border-border bg-background/40 p-3 text-sm text-foreground">
            <div
              className="prose prose-invert max-w-none text-sm"
              dangerouslySetInnerHTML={{
                __html:
                  highlightUnknownPlaceholders(previewLog.body_html || "") ||
                  "<p class='text-muted-foreground'>No body captured.</p>",
              }}
            />
          </div>
        </section>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <aside className="relative z-10 flex h-full w-full max-h-full max-w-full flex-col overflow-hidden border border-border bg-background/85 backdrop-blur-xl text-foreground shadow-2xl sm:h-full sm:max-h-[90vh] sm:max-w-5xl sm:rounded-3xl">
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Email compose</p>
            <h2 className="text-2xl font-semibold text-foreground">{contact?.name ?? lead?.title ?? "New message"}</h2>
            {contact?.email ? (
              <p className="text-xs text-muted-foreground">{contact.email}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Add a recipient email below.</p>
            )}
            {contactSourceHref && (
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <span>Source URL:</span>
                <a
                  href={contactSourceHref}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline decoration-primary/70 underline-offset-2 hover:text-foreground"
                >
                  {contactSourceUrl}
                </a>
                <a
                  href={contactSourceHref}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="rounded-full border border-border bg-background/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground transition-all duration-200 hover:border-primary/60 hover:text-primary"
                >
                  Go to Source URL
                </a>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {sequenceControls && (
              <div className="flex items-center gap-1 rounded-full border border-border/70 px-2 py-1 text-xs text-muted-foreground">
                <button
                  type="button"
                  onClick={sequenceControls.onPrev}
                  disabled={!sequenceControls.onPrev}
                  className="rounded-full p-2 text-foreground/70 transition hover:text-foreground disabled:opacity-40 disabled:hover:text-muted-foreground sm:p-1"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-[11px] text-muted-foreground">
                  {sequenceControls.index + 1} / {sequenceControls.total}
                </span>
                <button
                  type="button"
                  onClick={sequenceControls.onNext}
                  disabled={!sequenceControls.onNext}
                  className="rounded-full p-2 text-foreground/70 transition hover:text-foreground disabled:opacity-40 disabled:hover:text-muted-foreground sm:p-1"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
            {showFavoriteAction && allowTemplateAutomation && (
              <button
                type="button"
                onClick={handleFavoriteDraft}
                disabled={favoriteBusy}
                title="Save this draft as a template"
                className={`rounded-full border px-3 py-1 text-xs uppercase tracking-wide transition ${
                  favoriteBusy
                    ? "border-primary text-primary"
                    : "border-primary/60 text-primary hover:bg-primary/10"
                }`}
              >
                <Star className="mr-1 inline h-4 w-4" />
                Favorite
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-border px-3 py-1 text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
            >
              Close
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
            <div className="space-y-4">
            <section className="space-y-3">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                To
                <input
                  type="email"
                  value={toInput}
                  onChange={(event) => {
                    setToInput(event.target.value);
                    setOutreachDirty(true);
                  }}
                  aria-label="To"
                  className="mt-2 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                />
              </label>
            </section>
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Templates</p>
                {aiEnabled && (
                  <CapabilityGate flag="canUseCrmEmailAI">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs text-foreground/70 transition-all duration-200 hover:border-primary/60 hover:text-primary disabled:opacity-50"
                        disabled={!canGenerateDraft || generating}
                        title={!canGenerateDraft ? "Select or create a lead to enable AI drafting" : undefined}
                        onClick={handleGenerateDraft}
                      >
                        <Sparkles className="h-3 w-3" />
                        {generating ? "Generating…" : "Generate draft"}
                      </button>
                      {selectedTemplate && (
                        <button
                          type="button"
                          className="text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            setSelectedTemplateId(null);
                            setSubjectTouched(true);
                            setBodyTouched(true);
                          }}
                        >
                          Detach from template
                        </button>
                      )}
                    </div>
                  </CapabilityGate>
                )}
              </div>
              <TemplatePicker
                templates={pickerTemplates}
                loading={templatesLoading}
                selectedId={selectedTemplateId}
                onSelect={(template: EmailTemplateOption | null) => {
                  if (isOutreach) {
                    if (template) void handleApplyOutreachTemplate(template);
                    return;
                  }
                  if (template) {
                    setSubjectTouched(false);
                    setBodyTouched(false);
                  } else {
                    setSubjectTouched(true);
                    setBodyTouched(true);
                  }
                  setSelectedTemplateId(template?.id ?? null);
                }}
              />
            </section>
            {isOutreach && (
              <section className="space-y-2 text-xs" data-testid="outreach-template-tools">
                <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={() => setManagerOpen(true)}>
                  Manage templates
                </button>
                {fill && fill.missing.length > 0 && (
                  <p role="status" data-testid="outreach-missing" className="rounded-md bg-amber-500/10 px-3 py-2 text-amber-800 dark:text-amber-200">
                    Fill these in by hand before sending — we don't have them yet: {fill.missing.map((name) => `{{${name}}}`).join(", ")}.
                  </p>
                )}
                {fill && fill.unknown.length > 0 && (
                  <p role="status" data-testid="outreach-unknown" className="text-muted-foreground">
                    Not a supported variable: {fill.unknown.map((name) => `{{${name}}}`).join(", ")}.
                  </p>
                )}
              </section>
            )}
            {!isOutreach && (
            <Accordion type="single" collapsible defaultValue="variables">
              <AccordionItem value="variables" className="aa-glass-card border-none">
                <AccordionTrigger className="px-4 py-3 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  Variables
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Merge fields</p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setContextRows((rows) => [...rows, { key: "", value: "" }])}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                      >
                        <Plus className="h-3 w-3" /> Add field
                      </button>
                      <button
                        type="button"
                        onClick={handleAutoFillVariables}
                        disabled={registryLoading}
                        className="rounded-full border border-border px-3 py-1 text-[11px] uppercase tracking-wide text-foreground/70 transition-all duration-200 hover:border-primary/60 hover:text-primary disabled:opacity-50"
                      >
                        Auto-fill
                      </button>
                    </div>
                  </div>
                  {registryError && <p className="mb-2 text-xs text-destructive">{registryError}</p>}
                  <div className="space-y-2">
                {contextRows.map((row, index) => (
                  <div key={`${row.key}-${index}`} className="flex gap-2 text-sm">
                        <input
                          type="text"
                          value={row.key}
                          placeholder="key"
                          onChange={(event) => updateContextRow(index, { ...row, key: event.target.value })}
                          className="w-1/3 rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                        />
                        <input
                          type="text"
                          value={row.value}
                          placeholder="value"
                          onChange={(event) => updateContextRow(index, { ...row, value: event.target.value })}
                          className="flex-1 rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                        />
                        <button
                          type="button"
                          className="rounded-full border border-transparent p-1 text-muted-foreground hover:text-destructive"
                          onClick={() => removeContextRow(index)}
                        >
                          <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Artist assets</p>
                <div className="flex flex-wrap gap-2">
                  {artistAssetTokens.map((token) => (
                    <button
                      key={token}
                      type="button"
                      onClick={() => handleInsertToken(token)}
                      className="rounded-full border border-border bg-background/40 px-2 py-1 text-[11px] text-foreground transition-all duration-200 hover:border-primary/70 hover:bg-primary/10"
                    >
                      {token}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Artist socials</p>
                <div className="flex flex-wrap gap-2">
                  {artistSocialTokens.map((token) => (
                    <button
                      key={token}
                      type="button"
                      onClick={() => handleInsertToken(token)}
                      className="rounded-full border border-border bg-background/40 px-2 py-1 text-[11px] text-foreground transition-all duration-200 hover:border-primary/70 hover:bg-primary/10"
                    >
                      {token}
                    </button>
                  ))}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
            )}
            {allowTemplateAutomation && (templateWarningEntries.length > 0 || templateVariables) && (
              <CapabilityGate flag="canUseCrmEmailAI">
                <TemplateWarnings warnings={templateWarningEntries} variables={templateVariables} />
              </CapabilityGate>
            )}
            {aiEnabled && (
              <CapabilityGate flag="canUseCrmEmailAI">
                <CapabilityGate flag="canUseEmailRevisionTools" variant="hide">
                  {revisionDiff.length > 0 && (
                    <section className="aa-glass-card p-4 text-xs">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Revision diff</p>
                      <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                        {revisionDiff.map((segment, index) => (
                          <p
                            key={`diff-${index}-${segment.type}`}
                            className={`whitespace-pre-wrap rounded px-2 py-1 ${
                              segment.type === "added"
                                ? "bg-success/10 text-success"
                                : segment.type === "removed"
                                  ? "bg-destructive/10 text-destructive line-through"
                                  : "text-foreground/70"
                            }`}
                          >
                            {segment.text || " "}
                          </p>
                        ))}
                      </div>
                    </section>
                  )}
                </CapabilityGate>
              </CapabilityGate>
            )}
            <section className="space-y-3">
              <label className="text-xs text-muted-foreground">
                <span className="uppercase tracking-wide">Subject</span>
                <input
                  type="text"
                  value={subjectInput}
                  onChange={(event) => {
                    setSubjectTouched(true);
                    setSubjectInput(event.target.value);
                    setOutreachDirty(true);
                  }}
                  aria-label="Subject"
                  disabled={templateLocked}
                  className="mt-2 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground disabled:text-muted-foreground"
                />
              </label>
              <label className="text-xs text-muted-foreground">
                <span className="uppercase tracking-wide">Body</span>
                {templateLocked ? (
                  <div className="mt-2 rounded-lg border border-border bg-background/60 p-3 text-sm text-foreground">
                    <div
                      className="prose prose-invert max-w-none text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html:
                          sanitizeTemplateHtml(highlightUnknownPlaceholders(bodyInput || "")) ||
                          "<p class='text-muted-foreground'>No template body.</p>",
                      }}
                    />
                    <p className="mt-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                      Template body preview (context replaces highlighted tokens).
                    </p>
                  </div>
                ) : (
                  <RichTextEditor
                    value={bodyInput}
                    onChange={(value) => {
                      setBodyTouched(true);
                      setBodyInput(value);
                      setOutreachDirty(true);
                    }}
                    ariaLabel="Message"
                    minHeight={250}
                    className="mt-2"
                  />
                )}
              </label>
            </section>
            {allowTemplateAutomation && selectedTemplate?.metadata && (
              <CapabilityGate flag="canUseCrmEmailAI">
                <section className="aa-glass-card p-4 text-xs text-foreground/70">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Template metadata</p>
                  <pre className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-border bg-background/40 p-3 text-[11px] text-foreground/80">
                    {JSON.stringify(selectedTemplate.metadata, null, 2)}
                  </pre>
                </section>
              </CapabilityGate>
            )}
            </div>
            <div className="hidden lg:block">{sidebarInsights}</div>
          </div>
          <div className="mt-4 lg:hidden">
            <button
              type="button"
              className="flex w-full items-center justify-between aa-glass-card px-3 py-2 text-sm text-foreground/80"
              onClick={() => setMobileSidebarOpen((prev) => !prev)}
            >
              <span>Insights & history</span>
              <span className="text-xs text-muted-foreground">{mobileSidebarOpen ? "Hide" : "Show"}</span>
            </button>
            {mobileSidebarOpen && <div className="mt-3 space-y-4">{sidebarInsights}</div>}
          </div>
        </div>
        {isOutreach ? (
          <footer className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background/95 px-4 py-4 backdrop-blur sm:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="secondary" onClick={() => void handleCopyOutreach()} disabled={!hasOutreachDraft}>
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleSaveOutreachDraft()}
                disabled={!hasOutreachDraft || !outreachDirty || outreachBusy !== null}
              >
                {outreachBusy === "saving" ? "Saving…" : "Save draft"}
              </Button>
              {outreachMessage && (
                <span role="status" className="text-xs text-muted-foreground">
                  {outreachMessage}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
            {effectiveSender?.verification_status === "connected" ? (
              <Button
                type="button"
                className="min-h-[44px] px-6"
                disabled={!readyToSend || outreachBusy !== null || outreachSent}
                onClick={() => void handleSendOutreach()}
              >
                {outreachBusy === "sending" ? "Sending…" : outreachSent ? "Sent" : "Send email"}
              </Button>
            ) : (
              <span className="text-xs text-amber-300">Connect a verified sender in Account settings to send.</span>
            )}
            {outreach?.contacted || outreachSent ? (
              <span className="text-xs text-muted-foreground">Already contacted · in Outreach</span>
            ) : (
              outreach?.onMarkContacted && (
                <Button className="min-h-[44px] px-6" onClick={() => void handleMarkContacted()} disabled={outreachBusy !== null}>
                  {outreachBusy === "marking" ? "Updating…" : "Mark contacted"}
                </Button>
              )
            )}
            </div>
          </footer>
        ) : (
          <footer className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background/95 px-4 py-4 backdrop-blur sm:px-6">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="rounded-full px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => {
                  if (!readyToSend) {
                    push({ title: "Missing fields", message: "Add a recipient and content before sending." });
                    return;
                  }
                  void sendTest(buildPayload());
                }}
              >
                Send test to {toInput || "recipient"}
              </button>
              {allowEmailAI && lead?.requires_review && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void handleRequestReview();
                  }}
                >
                  Request review
                </Button>
              )}
            </div>
            <Button
              className="min-h-[44px] px-6"
              disabled={!readyToSend || sending}
              onClick={() => void handleSend()}
            >
              {sending ? "Sending…" : "Send email"}
            </Button>
          </footer>
        )}
      </aside>
      {isOutreach && (
        <TemplateManagerDrawer
          open={managerOpen}
          onClose={() => setManagerOpen(false)}
          onTemplatesChanged={() => void refreshTemplates()}
          allowSystemTemplates={false}
        />
      )}
    </div>
  );

  function updateContextRow(index: number, value: ContextRow) {
    setContextRows((rows) => rows.map((row, idx) => (idx === index ? value : row)));
  }

  function removeContextRow(index: number) {
    setContextRows((rows) => rows.filter((_, idx) => idx !== index));
  }
}

function deriveDefaultContext(contact?: CrmContact | null, lead?: CrmLead | null) {
  const ctx: Record<string, string> = {};
  if (contact?.contact_name) ctx.contact_name = contact.contact_name;
  ctx.company_name = contact?.company_name ?? ctx.company_name ?? "";
  ctx.venue_name = contact?.company_name ?? ctx.venue_name ?? "";
  ctx.city = contact?.location_city ?? ctx.city ?? "";
  ctx.region = contact?.location_region ?? ctx.region ?? "";
  ctx.country = contact?.country ?? ctx.country ?? "";
  ctx.venue_city = contact?.location_city ?? ctx.venue_city ?? "";
  ctx.venue_state = contact?.location_region ?? ctx.venue_state ?? "";
  ctx.venue_country = contact?.country ?? ctx.venue_country ?? "";
  if (lead?.title) ctx.lead_title = lead.title;
  if (lead?.region) ctx.lead_region = lead.region;
  if (lead?.title && !ctx.venue_name) {
    ctx.venue_name = lead.title;
  }
  const eventWindow = lead?.event_window ?? getEventWindowFromMetadata(lead?.metadata ?? null);
  if (typeof window !== "undefined" && lead?.id) {
    // Temporary debug for flow-1: trace what the drawer sees for event window.
    // Remove once confirmed.
    // eslint-disable-next-line no-console
    console.log("[EmailDrawer] lead event window", {
      leadId: lead.id,
      event_window: lead?.event_window,
      metadata_event_window: lead?.metadata?.["event_window"],
      metadata_window_start: lead?.metadata?.["window_start"],
      metadata_window_end: lead?.metadata?.["window_end"],
      metadata_routing_window_start: lead?.metadata?.["routing_window_start"],
      metadata_routing_window_end: lead?.metadata?.["routing_window_end"],
      derived_event_window: eventWindow,
    });
  }
  const startWindow = eventWindow?.start ?? "";
  const endWindow = eventWindow?.end ?? "";
  ctx.event_window_start = startWindow;
  ctx.event_window_end = endWindow;
  if (startWindow) {
    const date = new Date(startWindow);
    if (!Number.isNaN(date.getTime())) {
      ctx.event_window_start_pretty = date.toLocaleDateString();
    }
  }
  if (endWindow) {
    const date = new Date(endWindow);
    if (!Number.isNaN(date.getTime())) {
      ctx.event_window_end_pretty = date.toLocaleDateString();
    }
  }
  if (lead?.metadata && typeof lead.metadata === "object") {
    const maybeCity = String(lead.metadata["city"] ?? lead.metadata["venue_city"] ?? "");
    const maybeState = String(lead.metadata["state"] ?? lead.metadata["venue_state"] ?? "");
    if (maybeCity && !ctx.venue_city) {
      ctx.venue_city = maybeCity;
    }
    if (maybeState && !ctx.venue_state) {
      ctx.venue_state = maybeState;
    }
  }
  return ctx;
}

function buildArtistContext(artist?: ArtistSettings | null): Record<string, string> {
  if (!artist) return {};
  const ctx: Record<string, string> = {};
  const setPair = (dotKey: string, aliasKey: string, value?: string | null) => {
    const normalized = value ?? "";
    ctx[dotKey] = normalized;
    ctx[aliasKey] = normalized;
  };
  const genres = Array.isArray(artist.genres) ? artist.genres.filter(Boolean) : [];
  setPair("artist.name", "artist_name", artist.name);
  setPair("artist.genre", "artist_genre", genres[0] ?? "");
  setPair("artist.genres", "artist_genres", genres.join(", "));
  setPair("artist.website", "artist_website", artist.website);
  setPair("artist.instagram", "artist_instagram", artist.instagram);
  setPair("artist.facebook", "artist_facebook", artist.facebook);
  setPair("artist.x_url", "artist_x_url", artist.x_url);
  setPair("artist.youtube_url", "artist_youtube_url", artist.youtube_url);
  setPair("artist.tiktok", "artist_tiktok", artist.tiktok);
  setPair("artist.spotify_url", "artist_spotify_url", artist.spotify_url);
  setPair("artist.apple_music_url", "artist_apple_music_url", artist.apple_music_url);
  setPair("artist.bandcamp_url", "artist_bandcamp_url", artist.bandcamp_url);
  setPair("artist.bandsintown_url", "artist_bandsintown_url", artist.bandsintown_url);
  setPair("artist.band_photo_url", "artist_band_photo_url", artist.band_photo_url);
  setPair("artist.logo_url", "artist_logo_url", artist.logo_url);
  const videoLinks = artist.video_links ?? [];
  const audioLinks = artist.audio_links ?? [];
  setPair("artist.video_link_1", "artist_video_link_1", videoLinks[0] ?? "");
  setPair("artist.video_link_2", "artist_video_link_2", videoLinks[1] ?? "");
  setPair("artist.video_link_3", "artist_video_link_3", videoLinks[2] ?? "");
  setPair("artist.audio_link_1", "artist_audio_link_1", audioLinks[0] ?? "");
  setPair("artist.audio_link_2", "artist_audio_link_2", audioLinks[1] ?? "");
  setPair("artist.audio_link_3", "artist_audio_link_3", audioLinks[2] ?? "");
  return ctx;
}

function entriesFromContext(map: Record<string, string>) {
  return Object.entries(map).map(([key, value]) => ({ key, value }));
}

function mergeContext(base: Record<string, string>, overrides?: Record<string, string>) {
  if (!overrides) return base;
  const merged = { ...base };
  Object.entries(overrides).forEach(([key, value]) => {
    const current = merged[key];
    if (!current || String(current).trim() === "") {
      merged[key] = value;
    }
  });
  return merged;
}

function filterLogsByAddress(logs: EmailLog[], address?: string) {
  if (!address) return logs;
  const needle = address.toLowerCase();
  return logs.filter((log) => {
    const toAddress = log.to_address?.toLowerCase() ?? "";
    const fromAddress = log.from_address?.toLowerCase() ?? "";
    return toAddress.includes(needle) || fromAddress.includes(needle);
  });
}

function buildLineDiff(previous: string, next: string) {
  const prevLines = previous.split("\n");
  const nextLines = next.split("\n");
  const maxLength = Math.max(prevLines.length, nextLines.length);
  const segments: Array<{ type: "equal" | "added" | "removed"; text: string }> = [];
  for (let index = 0; index < maxLength; index += 1) {
    const prevLine = prevLines[index] ?? "";
    const nextLine = nextLines[index] ?? "";
    if (prevLine === nextLine) {
      segments.push({ type: "equal", text: prevLine });
      continue;
    }
    if (prevLine) {
      segments.push({ type: "removed", text: prevLine });
    }
    if (nextLine) {
      segments.push({ type: "added", text: nextLine });
    }
  }
  return segments;
}

function stripHtml(value: string): string {
  return value.replace(/<[^>]+>/g, " ");
}

function renderTemplate(template: string, context: Record<string, string>) {
  const withMustache = template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, token) => context[token] ?? "");
  return withMustache.replace(/\[([a-zA-Z0-9._-]+)\]/g, (_, token) => context[token] ?? "");
}

function detectBracketTokens(text: string) {
  if (!text) return [];
  const tokens = new Set<string>();
  const regex = /\[([a-zA-Z0-9._-]+)\]/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match[1]) {
      tokens.add(match[1]);
    }
  }
  return Array.from(tokens);
}
